import { z } from 'zod';

/**
 * Password policy — single source of truth for FE and (mirrored by) BE.
 *
 * Aligned with:
 *   - Backend: `config.password_complexity` in devise_security_full.rb and
 *     `config.password_length` in devise.rb.
 *   - CNBV / PCI-DSS v4 / OWASP 2024 banking-grade recommendations.
 *
 * Keep these constants stable: any change must be coordinated with the BE.
 */
export const PASSWORD_RULES = {
  /** Minimum length in characters. 12 is the fintech baseline. */
  minLength: 12,
  /** Maximum length in characters. 128 keeps bcrypt happy without hard-limiting users. */
  maxLength: 128,
  /** Require at least one uppercase letter (A–Z). */
  requireUppercase: true,
  /** Require at least one lowercase letter (a–z). */
  requireLowercase: true,
  /** Require at least one digit (0–9). */
  requireDigit: true,
  /** Require at least one non-alphanumeric character (symbol / punctuation). */
  requireSymbol: true,
} as const;

/**
 * Monolithic regex enforcing the full policy. Useful outside Zod (HTML
 * `pattern` attribute, plain-text utils, quick `.test()` checks).
 *
 * The character class `[^A-Za-z0-9]` intentionally accepts any non-alphanumeric
 * including common keyboard symbols and Unicode punctuation so international
 * users aren't blocked.
 */
export const PASSWORD_REGEX = /^(?=.*[A-Z])(?=.*[a-z])(?=.*\d)(?=.*[^A-Za-z0-9]).{12,128}$/;

/**
 * Per-rule regexes. Exported for UI feedback (show which rule is met live).
 */
export const PASSWORD_RULE_REGEXES = {
  uppercase: /[A-Z]/,
  lowercase: /[a-z]/,
  digit: /\d/,
  symbol: /[^A-Za-z0-9]/,
} as const;

/**
 * Zod schema. Each `.regex()` uses an i18n key as its message so the caller
 * can resolve translations via `t()` when formatting errors.
 *
 * Order of validations matters for error precedence. We surface `minLength`
 * first (most common failure), then character-class checks.
 */
export const passwordSchema = z
  .string()
  .min(PASSWORD_RULES.minLength, 'password.errors.minLength')
  .max(PASSWORD_RULES.maxLength, 'password.errors.maxLength')
  .regex(PASSWORD_RULE_REGEXES.uppercase, 'password.errors.uppercase')
  .regex(PASSWORD_RULE_REGEXES.lowercase, 'password.errors.lowercase')
  .regex(PASSWORD_RULE_REGEXES.digit, 'password.errors.digit')
  .regex(PASSWORD_RULE_REGEXES.symbol, 'password.errors.symbol');

/**
 * Granular per-rule evaluation for live UI feedback (the "checklist" pattern
 * where each rule lights up green as the user types).
 *
 * Returns `true` for each rule the password satisfies. Never throws.
 */
export function checkPasswordRules(password: string): {
  minLength: boolean;
  hasUppercase: boolean;
  hasLowercase: boolean;
  hasDigit: boolean;
  hasSymbol: boolean;
  /** True if all rules above are met. */
  allMet: boolean;
} {
  const minLength = password.length >= PASSWORD_RULES.minLength;
  const hasUppercase = PASSWORD_RULE_REGEXES.uppercase.test(password);
  const hasLowercase = PASSWORD_RULE_REGEXES.lowercase.test(password);
  const hasDigit = PASSWORD_RULE_REGEXES.digit.test(password);
  const hasSymbol = PASSWORD_RULE_REGEXES.symbol.test(password);
  return {
    minLength,
    hasUppercase,
    hasLowercase,
    hasDigit,
    hasSymbol,
    allMet: minLength && hasUppercase && hasLowercase && hasDigit && hasSymbol,
  };
}

/**
 * Ordinal strength estimate for UI meters. Scale 0–4 matches zxcvbn so we can
 * swap to a real estimator without touching consumers.
 *
 * This heuristic is a baseline used when zxcvbn-ts isn't loaded yet (first
 * paint). Consumers rendering a strength meter should prefer zxcvbn-ts for
 * the actual score.
 */
export type PasswordStrength = 0 | 1 | 2 | 3 | 4;

export function estimatePasswordStrengthHeuristic(password: string): PasswordStrength {
  if (!password) return 0;
  const rules = checkPasswordRules(password);
  const passedRules = [
    rules.hasUppercase,
    rules.hasLowercase,
    rules.hasDigit,
    rules.hasSymbol,
  ].filter(Boolean).length;
  const lengthBonus = password.length >= 16 ? 1 : 0;
  // Keep at 0 when everything is missing; clamp at 4.
  const raw = (rules.minLength ? 1 : 0) + passedRules - 1 + lengthBonus;
  return Math.max(0, Math.min(4, raw)) as PasswordStrength;
}
